import { useMemo } from "react";
import {
  Background, ControlButton, Controls, Handle, Position, ReactFlow,
  type Edge, type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { criticalPath, LOCATION_NODE } from "./TemplateGraphEditor";
import { useGraphFullscreen } from "./useGraphFullscreen";
import type { ProcessTemplate, SlaRule } from "./types";

/**
 * Read-only mapa kamieni z DOMYŚLNEGO szablonu procesu dla zakładki „Terminy”:
 * każdy kamień to blok z sekwencją faz (1 = nasz ruch, 2 = odpowiedź urzędu),
 * sub-SLA przy każdej fazie, zależnościami jako strzałki (Dagre LR) i ścieżką
 * krytyczną na pomarańczowo. Zapasowe reguły „kamień milowy” (dla kroków bez faz)
 * doklejają się do stopki bloku — dzięki temu nie potrzebują osobnej tabeli.
 */

const NODE_W = 250;

type SeqNodeData = {
  label: string;
  stepKey: string;
  submitLabel: string;
  submitDays: number;
  resolveLabel: string;
  resolveDays: number;
  fallback: { sla: number; warn: number } | null;
  synthetic: boolean;
  isCritical: boolean;
  isSelected: boolean;
  clickable: boolean;
};

const _lines = (text: string, perLine: number) => Math.max(1, Math.ceil((text ?? "").length / perLine));

function nodeHeight(d: SeqNodeData): number {
  let h = 24; // padding + klucz
  h += _lines(d.label, 24) * 15;
  h += 8 + _lines(d.submitLabel || "—", 26) * 13 + 14; // faza 1
  if (d.resolveLabel || d.resolveDays) h += 10 + _lines(d.resolveLabel || "—", 26) * 13 + 14; // ↓ + faza 2
  if (d.fallback) h += 16;
  return h + 12;
}

function PhaseRow({ no, side, label, days }: { no: 1 | 2; side: "MY" | "URZĄD"; label: string; days: number }) {
  const ours = side === "MY";
  return (
    <div
      className="flex items-center gap-1.5 rounded px-1.5 py-1 mt-1"
      style={{
        background: ours ? "#eff6ff" : "#fffbeb",
        border: `1px solid ${ours ? "#bfdbfe" : "#fde68a"}`,
      }}
    >
      <span
        className="shrink-0 font-mono text-[9px] font-bold px-1 rounded"
        style={{ background: ours ? "#3b82f6" : "#d97706", color: "#fff" }}
        title={ours ? "Faza 1 — nasz ruch (zegar biegnie nam)" : "Faza 2 — odpowiedź urzędu/instytucji (zegar biegnie im)"}
      >
        {no}·{side}
      </span>
      <span className="flex-1 text-[10px] leading-tight whitespace-normal break-words" style={{ color: "#334155" }}>
        {label || "—"}
      </span>
      <span className="shrink-0 font-mono text-[10px] font-bold" style={{ color: ours ? "#1d4ed8" : "#b45309" }}>
        {days}d
      </span>
    </div>
  );
}

function SeqNode({ data }: { data: SeqNodeData }) {
  const hasResolve = !!(data.resolveLabel || data.resolveDays);
  const total = (data.submitDays || 0) + (data.resolveDays || 0);
  return (
    <div
      className="rounded-lg border shadow-sm"
      style={{
        width: NODE_W, padding: "8px 10px",
        background: data.synthetic ? "#f5f3ff" : "#f8fafc",
        borderColor: data.isSelected ? "var(--color-primary)"
          : data.synthetic ? "#7c3aed" : data.isCritical ? "#ea580c" : "rgba(0,0,0,0.15)",
        borderWidth: data.isSelected || data.isCritical || data.synthetic ? 2 : 1,
        borderStyle: data.synthetic ? "dashed" : "solid",
        cursor: data.clickable && !data.synthetic ? "pointer" : "default",
      }}
      title={data.clickable && !data.synthetic ? "Kliknij, żeby edytować terminy tego kamienia" : undefined}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0, width: 6, height: 6 }} />
      <div className="flex items-start gap-1">
        <span className="flex-1 text-xs font-bold leading-tight whitespace-normal break-words" style={{ color: data.synthetic ? "#5b21b6" : "#0f172a" }}>
          {data.label}
          {data.isCritical && <span title="Ścieżka krytyczna (najdłuższa suma SLA)" style={{ color: "#ea580c" }}> ◆</span>}
        </span>
        {!data.synthetic && (
          <span className="shrink-0 font-mono text-[10px] font-bold" style={{ color: "#64748b" }} title="Suma SLA obu faz kroku">
            Σ{total}d
          </span>
        )}
      </div>
      <div className="font-mono text-[9px]" style={{ color: "#94a3b8" }}>{data.stepKey}</div>
      {data.synthetic ? (
        <div className="text-[10px] mt-1" style={{ color: "#5b21b6" }}>
          węzeł syntetyczny — spina uzgodnienia lokalizacji (działki / WPW)
        </div>
      ) : (
        <>
          <PhaseRow no={1} side="MY" label={data.submitLabel} days={data.submitDays} />
          {hasResolve && (
            <>
              <div className="text-center leading-none text-[10px]" style={{ color: "#94a3b8" }}>↓</div>
              <PhaseRow no={2} side="URZĄD" label={data.resolveLabel} days={data.resolveDays} />
            </>
          )}
          {data.fallback && (
            <div
              className="font-mono text-[9px] mt-1"
              style={{ color: "#94a3b8" }}
              title="Reguła zapasowa: obowiązuje tylko, gdy krok projektu nie ma zdefiniowanych faz."
            >
              zapas bez faz: {data.fallback.sla}d · ostrz. {data.fallback.warn}d
            </div>
          )}
        </>
      )}
      <Handle type="source" position={Position.Right} style={{ opacity: 0, width: 6, height: 6 }} />
    </div>
  );
}

const nodeTypes = { seqStep: SeqNode };

function layout(
  template: ProcessTemplate, fallbackByKey: Map<string, SlaRule>,
  selectedKey: string | null, clickable: boolean,
): { nodes: Node[]; edges: Edge[]; critDays: number | null; critLabels: string[] } {
  const crit = criticalPath(template.steps, template.dependencies);
  const critKeys = new Set(crit?.path ?? []);
  const critEdges = new Set((crit?.path ?? []).slice(1).map((k, i) => `${crit!.path[i]}->${k}`));

  const datas: { id: string; data: SeqNodeData }[] = template.steps.map((s, i) => {
    const fb = s.key ? fallbackByKey.get(s.key) : undefined;
    return {
      id: `s${i}`,
      data: {
        label: s.label || s.key,
        stepKey: s.key,
        submitLabel: s.submit_label,
        submitDays: s.submit_sla_days || 0,
        resolveLabel: s.resolve_label,
        resolveDays: s.resolve_sla_days || 0,
        fallback: fb ? { sla: fb.sla_days, warn: fb.warn_days } : null,
        synthetic: false,
        isCritical: !!s.key && critKeys.has(s.key),
        isSelected: !!s.key && s.key === selectedKey,
        clickable,
      },
    };
  });
  const usesLocation = template.dependencies.some(
    (d) => d.node === LOCATION_NODE || d.depends_on === LOCATION_NODE,
  );
  if (usesLocation) {
    datas.push({
      id: `key:${LOCATION_NODE}`,
      data: {
        label: "Uzgodnienie lokalizacji", stepKey: LOCATION_NODE,
        submitLabel: "", submitDays: 0, resolveLabel: "", resolveDays: 0,
        fallback: null, synthetic: true, isCritical: critKeys.has(LOCATION_NODE),
        isSelected: false, clickable: false,
      },
    });
  }

  const byKey = new Map<string, string>();
  template.steps.forEach((s, i) => { if (s.key && !byKey.has(s.key)) byKey.set(s.key, `s${i}`); });
  if (usesLocation) byKey.set(LOCATION_NODE, `key:${LOCATION_NODE}`);

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 24, ranksep: 70, marginx: 12, marginy: 12 });
  datas.forEach((n) => g.setNode(n.id, { width: NODE_W, height: nodeHeight(n.data) }));
  const edges: Edge[] = [];
  template.dependencies.forEach((d, i) => {
    const source = byKey.get(d.depends_on);
    const target = byKey.get(d.node);
    if (!source || !target) return;
    g.setEdge(source, target);
    const onCritical = critEdges.has(`${d.depends_on}->${d.node}`);
    edges.push({
      id: `d${i}`, source, target,
      animated: onCritical,
      style: { stroke: onCritical ? "#ea580c" : "#94a3b8", strokeWidth: onCritical ? 2.5 : 1.5 },
      markerEnd: { type: "arrowclosed" as const, color: onCritical ? "#ea580c" : "#94a3b8" },
    });
  });
  Dagre.layout(g);
  const nodes: Node[] = datas.map((n) => {
    const p = g.node(n.id);
    return {
      id: n.id, type: "seqStep",
      position: { x: p.x - NODE_W / 2, y: p.y - nodeHeight(n.data) / 2 },
      data: n.data as unknown as Record<string, unknown>,
      draggable: false, connectable: false, selectable: false,
    };
  });
  return { nodes, edges, critDays: crit?.days ?? null, critLabels: crit?.labels ?? [] };
}

export function MilestoneSequenceBoard({ template, milestoneRules, selectedKey = null, onStepClick }: {
  template: ProcessTemplate;
  /** Reguły SLA applies_to="milestone" (zapas dla kroków bez faz) — doklejane do bloków. */
  milestoneRules: SlaRule[];
  /** Klucz kroku podświetlonego jako edytowany (panel edycji pod grafem). */
  selectedKey?: string | null;
  /** Klik w blok (poza syntetycznym) — otwiera edycję terminów kroku. */
  onStepClick?: (key: string) => void;
}) {
  const { isFull, toggle: toggleFull } = useGraphFullscreen();
  const { nodes, edges, critDays, critLabels } = useMemo(() => {
    const fallbackByKey = new Map(milestoneRules.filter((r) => r.key).map((r) => [r.key, r]));
    return layout(template, fallbackByKey, selectedKey, !!onStepClick);
  }, [template, milestoneRules, selectedKey, onStepClick]);

  return (
    <div className={isFull ? "fixed inset-0 z-40 bg-[var(--color-bg-base)] p-4 flex flex-col overflow-auto" : ""}>
      <div className={`${isFull ? "flex-1 min-h-[280px]" : "h-[460px]"} rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]`}>
        <ReactFlow
          key={isFull ? "fs" : "inline"}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          colorMode="system"
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          minZoom={0.25}
          onNodeClick={(_e, n) => {
            const d = n.data as SeqNodeData;
            if (onStepClick && d.stepKey && !d.synthetic) onStepClick(d.stepKey);
          }}
        >
          <Background gap={16} />
          <Controls showInteractive={false}>
            <ControlButton onClick={toggleFull} title={isFull ? "Zamknij pełny ekran (Esc)" : "Pełny ekran"}>
              {isFull ? "✕" : "⛶"}
            </ControlButton>
          </Controls>
        </ReactFlow>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[10px] font-mono text-[var(--color-text-muted)]">
        <span><span className="inline-block px-1 rounded text-white" style={{ background: "#3b82f6" }}>1·MY</span> nasz ruch (zegar nam)</span>
        <span><span className="inline-block px-1 rounded text-white" style={{ background: "#d97706" }}>2·URZĄD</span> odpowiedź strony zewnętrznej (zegar im)</span>
        <span>→ zależność (co musi być wcześniej)</span>
        <span style={{ color: "#ea580c" }}>◆ ścieżka krytyczna{critDays ? `: ~${critDays} dni` : ""}</span>
        {onStepClick && <span><b>klik w blok = edycja terminów</b></span>}
      </div>
      {critLabels.length > 0 && (
        <p className="text-[10px] font-mono mt-0.5" style={{ color: "#ea580c" }}>
          {critLabels.join(" → ")}
        </p>
      )}
    </div>
  );
}
